import React, { useMemo, useState } from 'react';
import { Task } from '../../../types/task';
import { PRIORITY_META, toDate, isoDay, startOfDay } from './taskMeta';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface Props {
  tasks: Task[];
  onView: (task: Task) => void;
  onUpdateDueDate?: (taskId: string, dueDate: Date | null) => void;
  onNewOnDay?: (day: Date) => void;
}

const WEEKDAYS = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

const TaskCalendar: React.FC<Props> = ({ tasks, onView, onUpdateDueDate, onNewOnDay }) => {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return startOfDay(d); });
  const [overDay, setOverDay] = useState<string | null>(null);

  const days = useMemo(() => {
    const first = new Date(cursor);
    const offset = (first.getDay() + 6) % 7; // hétfő = 0
    const start = new Date(first); start.setDate(first.getDate() - offset);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const byDay = useMemo(() => {
    const m = new Map<string, Task[]>();
    tasks.forEach(t => {
      const d = toDate(t.dueDate);
      if (!d) return;
      const k = isoDay(d);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    });
    return m;
  }, [tasks]);

  const todayKey = isoDay(new Date());
  const monthLabel = cursor.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' });
  const noDue = tasks.filter(t => !toDate(t.dueDate) && t.status !== 'completed' && t.status !== 'cancelled');

  const onDrop = (e: React.DragEvent, day: Date) => {
    e.preventDefault();
    setOverDay(null);
    const id = e.dataTransfer.getData('text/task-id');
    if (!id || !onUpdateDueDate) return;
    const t = tasks.find(x => x.id === id);
    const prev = t ? toDate(t.dueDate) : null;
    const next = new Date(day);
    if (prev) next.setHours(prev.getHours(), prev.getMinutes(), 0, 0); else next.setHours(9, 0, 0, 0);
    if (!prev || isoDay(prev) !== isoDay(next)) onUpdateDueDate(id, next);
  };

  return (
    <div className="space-y-3">
      <div className="crm-card overflow-hidden">
        <div className="px-3 py-2 flex items-center justify-between border-b border-[var(--crm-border)]">
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1))} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Előző hónap"><ChevronLeftIcon className="w-4 h-4" /></button>
            <button type="button" onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1))} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Következő hónap"><ChevronRightIcon className="w-4 h-4" /></button>
            <span className="ml-2 text-sm font-semibold capitalize text-gray-900 dark:text-gray-50">{monthLabel}</span>
          </div>
          <button type="button" onClick={() => { const d = new Date(); d.setDate(1); setCursor(startOfDay(d)); }} className="crm-btn crm-btn-ghost text-xs h-8">Ma</button>
        </div>
        <div className="grid grid-cols-7 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-[var(--crm-border)]">
          {WEEKDAYS.map(w => <div key={w} className="px-2 py-1.5 text-center">{w}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const k = isoDay(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const list = byDay.get(k) || [];
            const isToday = k === todayKey;
            return (
              <div
                key={k}
                onDragOver={(e) => { e.preventDefault(); if (overDay !== k) setOverDay(k); }}
                onDragLeave={() => setOverDay(null)}
                onDrop={(e) => onDrop(e, d)}
                onDoubleClick={() => onNewOnDay?.(d)}
                className={`min-h-[84px] sm:min-h-[104px] border-b border-r border-[var(--crm-border)] p-1 sm:p-1.5 ${i % 7 === 6 ? 'border-r-0' : ''} ${!inMonth ? 'bg-gray-50/60 dark:bg-gray-900/30' : ''} ${overDay === k ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
              >
                <div className={`text-[11px] tabular-nums mb-1 ${isToday ? 'inline-flex w-5 h-5 items-center justify-center rounded-full bg-gray-900 text-white dark:bg-white dark:text-gray-900 font-semibold' : inMonth ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}`}>{d.getDate()}</div>
                <div className="space-y-0.5">
                  {list.slice(0, 3).map(t => {
                    const pr = PRIORITY_META[t.priority] || PRIORITY_META.medium;
                    const done = t.status === 'completed' || t.status === 'cancelled';
                    return (
                      <button
                        key={t.id}
                        type="button"
                        draggable={!!onUpdateDueDate}
                        onDragStart={(e) => { e.dataTransfer.setData('text/task-id', t.id); }}
                        onClick={() => onView(t)}
                        title={t.title}
                        className={`w-full text-left flex items-center gap-1 px-1 py-0.5 rounded text-[11px] leading-tight truncate hover:bg-gray-100 dark:hover:bg-gray-700 ${done ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${pr.dot}`} />
                        <span className="truncate">{t.title}</span>
                      </button>
                    );
                  })}
                  {list.length > 3 && <div className="text-[10px] text-gray-400 px-1">+{list.length - 3} további</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {noDue.length > 0 && (
        <div className="crm-card p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Határidő nélkül ({noDue.length}) – húzd egy napra</div>
          <div className="flex flex-wrap gap-1.5">
            {noDue.map(t => {
              const pr = PRIORITY_META[t.priority] || PRIORITY_META.medium;
              return (
                <button key={t.id} type="button" draggable={!!onUpdateDueDate} onDragStart={(e) => e.dataTransfer.setData('text/task-id', t.id)} onClick={() => onView(t)}
                  className="inline-flex items-center gap-1.5 px-2 h-7 rounded-full border border-[var(--crm-border)] text-xs text-gray-700 dark:text-gray-200 hover:border-gray-400 max-w-[240px]">
                  <span className={`w-1.5 h-1.5 rounded-full ${pr.dot}`} /><span className="truncate">{t.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCalendar;
